import { useSubscription } from '@apollo/client';
import { useLoggedUser } from 'state/user';
import useSaveNodeInCache from 'components/canvas/hooks/use-save-node-in-cache';
import useRemoveNodeFromCache from 'components/canvas/hooks/use-remove-node-from-cache';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';
import useRemoveNodeFromSurfaceInCache from 'components/canvas/hooks/use-remove-node-from-surface-in-cache';
import useAddNodeToSurfaceInCache from 'components/canvas/hooks/use-add-node-to-surface-in-cache';
import useSurfaceRolesMap from 'routes/surface/hooks/use-surface-roles-map';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import useReFetchSurfaceData from 'routes/surface/hooks/use-refetch-surface-data';
import useUpdateNodePosition from 'components/canvas/hooks/use-update-node-position';
import useProtocolOperations, {
  useProtocolNodePositionOperations,
} from 'components/canvas/components/nodes/components/protocol-node/hooks/use-protocol-operations';
import { useTranslation } from 'i18n';
import { useHistory } from 'react-router-dom';
import surfaceNodes from 'graphql/surface-node/queries/surface-nodes';
import { surfaceNodeCreatedSubscription } from 'graphql/surface-node/subscriptions/surface-node-created-subscription';
import { surfaceNodeUpdatedSubscription } from 'graphql/surface-node/subscriptions/surface-node-updated-subscription';
import { surfaceNodeDeletedSubscription } from 'graphql/surface-node/subscriptions/surface-node-deleted-subscription';
import { surfaceNodePositionUpdatedSubscription } from 'graphql/surface-node/subscriptions/surface-node-position-updated-subscription';
import { surfaceNodePastedSubscription } from 'graphql/surface-node/subscriptions/surface-node-pasted-subscription';
import { surfaceNodeCutSubscription } from 'graphql/surface-node/subscriptions/surface-node-cut-subscription';
import { surfaceNodesLinkedSubscription } from 'graphql/surface-node/subscriptions/surface-nodes-linked-subscription';
import { surfaceNodesUnlinkedSubscription } from 'graphql/surface-node/subscriptions/surface-nodes-unlinked-subscription';
import { surfaceNodeSharedSubscription } from 'graphql/surface-node/subscriptions/surface-node-shared-subscription';
import { surfaceNodeRevokedSubscription } from 'graphql/surface-node/subscriptions/surface-node-revoked-subscription';
import { surfaceNodeMovedSubscription } from 'graphql/surface/subscriptions/surface-node-moved';
import { collaboratingUserRoleChangedOnSurfaceSubscription } from 'graphql/surface/subscriptions/collaborating-user-role-changed-on-surface';
import { surfaceNodesLinkSettingsUpdateSubscription } from 'graphql/surface-node/subscriptions/surface-nodes-link-settings-update-subscription';
import { surfaceNodeEjectedFromProtocolSubscription } from 'graphql/surface-node/subscriptions/surface-node-ejected-from-protocol-subscription';
import { surfaceNodeAddedToProtocolSubscription } from 'graphql/surface-node/subscriptions/surface-node-added-to-protocol-subscription';
import { subSurfaceNodeCopiedSubscription } from 'graphql/surface/subscriptions/sub-surface-node-copied';
import toast from 'services/toast';
import { compose, set } from 'lodash/fp';
import { isUndefined, isEmpty } from 'lodash';
import { SurfaceNodeType, SurfaceProtocolNode } from 'graphql/types';
import { AnyObject } from 'types';

export default function useSurfaceRealTimeUpdates(surface: string) {
  const { t } = useTranslation();
  const [loggedUser] = useLoggedUser();
  const saveNodeInCache = useSaveNodeInCache();
  const removeNodeFromCache = useRemoveNodeFromCache();
  const removeNodeFromSurfaceInCache = useRemoveNodeFromSurfaceInCache();
  const addNodeToSurfaceInCache = useAddNodeToSurfaceInCache();
  const fetchNodeById = useFetchNodeFromCacheById(surface);
  const { findNodeById } = useFindNodeInCache();
  const currentSurface = useCurrentSurface();
  const surfaceRoleMap = useSurfaceRolesMap();

  const history = useHistory();

  const { reFetchSurfaceDataAndUpdateCache } = useReFetchSurfaceData(surface);

  const { resizeProtocolToOriginalSize } = useProtocolOperations();

  useSubscription(surfaceNodeCreatedSubscription, {
    variables: { surface },
    onSubscriptionData: saveNodeOnSurface,
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodeUpdatedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: saveNodeOnSurface,
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodeDeletedSubscription, {
    variables: { surface },
    onSubscriptionData: (options) => {
      const deletedSurfaceNode =
        options.subscriptionData.data.surfaceNodeDeleted;

      deletedSurfaceNode.surface = { id: surface };

      removeNodeFromCache(deletedSurfaceNode);
    },
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodeAddedToProtocolSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    fetchPolicy: 'no-cache',
    onSubscriptionData: (options) => {
      const protocolNodeData = subscriptionDataResolver(
        options.subscriptionData.data,
      );

      const protocolNode = findNodeById(
        protocolNodeData?.id,
      ) as SurfaceProtocolNode;

      const containingNode = findNodeById(protocolNodeData?.contains?.id);

      if (containingNode) {
        saveNodeInCache(set('contains', containingNode, protocolNode));
      }
    },
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodeEjectedFromProtocolSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: (options) => {
      const protocolNode = subscriptionDataResolver(
        options.subscriptionData.data,
      ) as SurfaceProtocolNode;

      if (protocolNode) {
        const nodeFromCache = fetchNodeById(protocolNode?.id);

        const newProtocolNode = compose(
          set('contains', null),
          set('updatedAt', new Date().toISOString()),
        )(nodeFromCache);

        saveNodeInCache(newProtocolNode);

        resizeProtocolToOriginalSize(newProtocolNode);
      }
    },
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodePositionUpdatedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: saveNodeOnSurface,
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodePastedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: (options) => {
      const surfaceNode = Object.entries(
        options.subscriptionData.data,
      ).reduce<any>((data, [_, value]) => value, {});

      if (surfaceNode.id) {
        addNodeToSurfaceInCache(surfaceNode);
        // setHighestZIndexToNode(surfaceNode);
      }
    },
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodeCutSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: (options) => {
      const cutSurfaceNode = options.subscriptionData.data.surfaceNodeCut;

      cutSurfaceNode.surface = { id: surface };

      removeNodeFromSurfaceInCache(cutSurfaceNode);
    },
    shouldResubscribe: true,
  });

  useSubscription(surfaceNodesLinkedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    fetchPolicy: 'no-cache',
    onSubscriptionData: (options) => {
      const { node: linkedNode, user } = Object.entries(
        options.subscriptionData.data,
      ).reduce<any>((data, [_, value]) => value, {});

      if (linkedNode) {
        const nodeFromCache = fetchNodeById(linkedNode.id);

        if (nodeFromCache) {
          if (user === loggedUser?.id) {
            saveNodeInCache(
              set(
                'pointsTo',
                nodeFromCache.pointsTo?.map(setNewLinkSettings),
                nodeFromCache,
              ),
            );
          } else {
            saveNodeInCache(
              set('pointsTo', linkedNode.pointsTo, nodeFromCache),
            );
          }
        }
      }

      function setNewLinkSettings(link) {
        const { settings } =
          linkedNode.pointsTo.find(findOtherDirectionLink) || {};

        return isEmpty(link.settings) ? set('settings', settings, link) : link;
      }

      function findOtherDirectionLink(link) {
        return function ({ from, to }) {
          return from.id === link.from.id && to.id === link.to.id;
        };
      }
    },
  });
  useSubscription(surfaceNodesUnlinkedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    fetchPolicy: 'no-cache',
    onSubscriptionData: (options) => {
      const { node: unLinkedNode } = Object.entries(
        options.subscriptionData.data,
      ).reduce<any>((data, [_, value]) => value, {});

      if (unLinkedNode) {
        const nodeFromCache = fetchNodeById(unLinkedNode.id);

        nodeFromCache &&
          saveNodeInCache(
            set('pointsTo', unLinkedNode.pointsTo, nodeFromCache),
          );
      }
    },
  });

  useSubscription(surfaceNodesLinkSettingsUpdateSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: (options) => {
      const data = Object.entries(options.subscriptionData.data).reduce<any>(
        (data, [_, value]) => value,
        {},
      );

      const fromNode = fetchNodeById(data?.from?.id);
      const toNode = fetchNodeById(data?.from?.id);

      function updateLinkSettingsInCache({ from, to }) {
        const newPointsTo = from.pointsTo.map((link) => {
          if (link.to.id === to.id) {
            return { ...link, settings: data.settings };
          }

          return link;
        });

        saveNodeInCache(set('pointsTo', newPointsTo, from));
      }

      if (fromNode && toNode) {
        updateLinkSettingsInCache({ from: fromNode, to: toNode });
        updateLinkSettingsInCache({ from: toNode, to: fromNode });
      }

      // TODO what if they are on another surface?
    },
  });

  useSubscription(surfaceNodeSharedSubscription, {
    variables: { surface },
    onSubscriptionData: async (options) => {
      const data = Object.entries(options.subscriptionData.data).reduce<any>(
        (data, [_, value]) => value,
        {},
      );

      if (data.surface) {
        return saveNodeInCache(data.surface);
      }

      await surfaceNodes({ input: { filter: { surface } } });
    },
  });

  useSubscription(surfaceNodeRevokedSubscription, {
    variables: { user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: (options) => {
      // If user is on the surface...redirect to home
      // Remove revoked surface from cache
      // Remove revoked user reference from cache

      const {
        revokedSurface,
        sharedReference,
      } = options.subscriptionData.data.surfaceNodeRevoked;

      if (revokedSurface === surface) {
        history.push('/');
        toast.success(
          t('Your access to surface {{name}} has been revoked', {
            replace: { name: currentSurface.name },
          }),
        );
      }

      const revokedSurfaceNode = fetchNodeById(revokedSurface);

      const sharedReferenceNode = fetchNodeById(sharedReference);

      if (revokedSurfaceNode) {
        const hasRoleOnParentSurface =
          revokedSurfaceNode?.surface?.loggedUserRole;

        if (hasRoleOnParentSurface) {
          // TODO save node in cache to propagate updates that node is not readable anymore
          surfaceNodes({ input: { filter: { surface } } });
        } else {
          removeNodeFromCache(revokedSurfaceNode);
        }
      }

      if (sharedReferenceNode) {
        removeNodeFromCache(sharedReferenceNode);
      }
    },
  });

  const { updateNodePositionInCache } = useUpdateNodePosition();

  const {
    updateProtocolNodePositionInCache,
  } = useProtocolNodePositionOperations();

  useSubscription(surfaceNodeMovedSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    fetchPolicy: 'no-cache',
    onSubscriptionData: (options) => {
      const data = options.subscriptionData.data.surfaceNodeMoved;

      const node = fetchNodeById(data.id);

      if (node.type === SurfaceNodeType.Protocol) {
        updateProtocolNodePositionInCache(node, data.position);
      } else {
        updateNodePositionInCache(node, data.position);
      }
    },
  });

  useSubscription(collaboratingUserRoleChangedOnSurfaceSubscription, {
    variables: { surface, user: loggedUser?.id },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: async (options) => {
      const data =
        options.subscriptionData.data.collaboratingUserRoleChangedOnSurface;

      await reFetchSurfaceDataAndUpdateCache();

      toast.success(
        t('Your role on surface has been changed to {{role}}', {
          replace: { role: surfaceRoleMap[data.role] },
        }),
      );
    },
  });

  useSubscription(subSurfaceNodeCopiedSubscription, {
    variables: { surface },
    skip: isUndefined(loggedUser?.id),
    onSubscriptionData: async (options) => {
      const surface = subscriptionDataResolver(options.subscriptionData.data);

      await reFetchSurfaceDataAndUpdateCache().then(() => {
        toast.success(
          t(`Successfully copied surface {{name}}`, {
            replace: { name: surface?.name },
          }),
        );
      });
    },
  });

  function saveNodeOnSurface(options) {
    const surfaceNode = Object.entries(
      options.subscriptionData.data,
    ).reduce<any>((data, [_, value]) => value, {});

    if (surfaceNode.id) {
      saveNodeInCache(surfaceNode);

      // setHighestZIndexToNode(surfaceNode);
    }
  }
}

function subscriptionDataResolver(data: AnyObject): AnyObject | null {
  if (data) {
    return Object.entries(data).reduce<AnyObject>(
      (data, [_, value]) => value,
      {},
    );
  }

  return null;
}
